import { motion } from 'framer-motion';
import { ChevronRight, ChevronLeft, ArrowRight, Pencil } from 'lucide-react';
import InfoSpot from '@/components/InfoSpot';
import PortalShellV2 from '@/components/PortalShellV2';
import AutoCursor from '@/components/AutoCursor';

function Stepper({ active }) {
  const steps = ['Basic Details', 'Assign Roles', 'Review'];
  return (
    <div className="px-6 py-4 bg-[#F7F7F7] rounded-xl mb-6">
      <div className="flex items-center justify-between max-w-2xl mx-auto">
        {steps.map((s, i) => (
          <div key={s} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${
                i < active ? 'bg-teal-600 border-teal-600' :
                i === active ? 'bg-white border-teal-600' :
                'bg-white border-[#D1D5DB]'
              }`}>
                {i < active
                  ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  : i === active
                  ? <div className="w-1.5 h-1.5 rounded-full bg-teal-600" />
                  : null}
              </div>
              <span className={`text-[11.5px] font-body whitespace-nowrap ${i === active ? 'font-bold text-[#0A0A0A]' : i < active ? 'text-[#0A0A0A] font-semibold' : 'text-[#6B7280]'}`}>{s}</span>
            </div>
            {i < steps.length - 1 && <div className={`flex-1 h-[2px] mx-2 mt-[-16px] ${i < active ? 'bg-teal-600' : 'bg-[#E5E7EB]'}`} />}
          </div>
        ))}
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[11.5px] font-body font-bold text-[#0A0A0A] mb-1">{label}</p>
      <p className="text-[12px] font-body text-[#374151]">{value}</p>
    </div>
  );
}

export default function UserFormReviewV2Scene({ onNext }) {
  const cursorPath = [
    { x: 400, y: 280, delay: 0.8 },
    { x: 400, y: 440, delay: 1.8 },
    { x: 400, y: 640, delay: 1.8 },
    { x: 450, y: 780, delay: 1.6, click: true },
  ];

  return (
    <PortalShellV2 variant="headerOnly" overlay={<AutoCursor targets={cursorPath} startDelay={0.5} />}>
      <div data-testid="scene-user-form-review-v2" className="px-10 pt-6 pb-8">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[11.5px] font-body text-[#6B7280] mb-1.5 flex items-center gap-1">
            Manage <ChevronRight size={10} className="inline" /> Users <ChevronRight size={10} className="inline" /> <span className="text-[#0A0A0A]">Add new user</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
            className="font-heading font-extrabold text-[30px] leading-tight tracking-tight text-[#0A0A0A] flex items-center gap-2"
          >
            Add New User
            <InfoSpot
              text="Step 3 of 3 — Review all details before sending the invite."
              position="right"
              autoOpenAt={0.8}
              autoCloseAfter={2.5}
              glowAt={0.5}
            />
          </motion.h1>
          <p className="mt-0.5 font-body text-[12px] text-[#6B7280]">Create a new user and set access permissions</p>

          <div className="border-b border-[#E5E7EB] mt-4 mb-5" />

          <Stepper active={2} />

          <button className="flex items-center gap-1 text-[12px] font-body text-[#0A0A0A] mb-4 hover:text-teal-700">
            <ChevronLeft size={12} /> Back
          </button>

          {/* Basic details panel */}
          <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="rounded-xl bg-[#F7F7F7] p-5 mb-4 relative"
          >
            <button className="absolute top-4 right-4 text-[#6B7280] hover:text-teal-700" title="Edit">
              <Pencil size={13} />
            </button>
            <h2 className="font-heading font-extrabold text-[16px] text-[#0A0A0A] mb-0.5">Basic details</h2>
            <p className="font-body text-[11px] text-[#6B7280] mb-4">Fill in the required basic information</p>

            <div className="grid grid-cols-2 gap-x-8 gap-y-3.5">
              <Field label="First name"             value="Abdul" />
              <Field label="Last name"              value="Ahmed" />
              <Field label="Username"               value="abdulahmed@abc.com" />
              <Field label="Business email address" value="abdulahmed@abc.com" />
              <Field label="Contact number"         value="+971 (123) 456 7890" />
              <Field label="User type"              value="Admin" />
              <Field label="Nationality"            value="UAE" />
              <Field label="Emirates ID number"     value="748-1234-1234567-1" />
              <Field label="Organisation"           value="Nextcare" />
              <Field label="Linked facility"        value="Rashid hospital, UAE | Rasheed pharmacy" />
            </div>
          </motion.section>

          {/* Assign roles panel */}
          <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
            className="rounded-xl bg-[#F7F7F7] p-5 mb-6 relative"
          >
            <button className="absolute top-4 right-4 text-[#6B7280] hover:text-teal-700" title="Edit">
              <Pencil size={13} />
            </button>
            <h2 className="font-heading font-extrabold text-[16px] text-[#0A0A0A] mb-0.5">Assign roles</h2>
            <p className="font-body text-[11px] text-[#6B7280] mb-4">Define what this user can access and manage</p>

            <div className="space-y-3.5">
              <div>
                <p className="text-[12px] font-body font-bold text-[#0A0A0A] mb-1">Facility Admin</p>
                <p className="text-[12px] font-body text-[#374151]">Rashid Hospital Location A <span className="text-[#9CA3AF] mx-1">·</span> Rashid Hospital Location A</p>
              </div>
              <div>
                <p className="text-[12px] font-body font-bold text-[#0A0A0A] mb-1">Organisation Admin</p>
                <p className="text-[12px] font-body text-[#374151]">Rashid Pharmacy</p>
              </div>
            </div>
          </motion.section>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}
            className="flex justify-center"
          >
            <motion.button
              data-testid="wizard-add-review-v2"
              onClick={onNext}
              animate={{
                scale: [1, 1, 1.08, 0.97, 1.05, 1],
                boxShadow: [
                  '0 0 0 0 rgba(13,148,136,0)',
                  '0 0 0 0 rgba(13,148,136,0)',
                  '0 0 0 10px rgba(13,148,136,0.3)',
                  '0 0 0 0 rgba(13,148,136,0)',
                  '0 0 0 8px rgba(13,148,136,0.25)',
                  '0 0 0 0 rgba(13,148,136,0)',
                ],
              }}
              transition={{ duration: 2.4, delay: 3.6, times: [0, 0.2, 0.4, 0.6, 0.8, 1] }}
              className="flex items-center gap-2 px-10 py-2.5 rounded-xl bg-teal-600 text-white font-body font-bold text-[12.5px] hover:bg-teal-700"
            >
              Add <ArrowRight size={13} />
            </motion.button>
          </motion.div>
        </div>
      </div>
    </PortalShellV2>
  );
}
